/**
 * ValidationService - Input Validation and Schema Management
 * 
 * Provides comprehensive validation for:
 * - Initiative forms
 * - API responses
 * - User inputs
 * - File uploads
 * - Data integrity
 * 
 * @module services/ValidationService
 */

import EventBus, { AppEvents } from '../core/EventBus.js';
import Config from '../core/Config.js';

/**
 * Validation schemas for different data types
 */
const SCHEMAS = {
  initiative: {
    // Core initiative fields
    title: {
      required: true,
      type: 'string',
      minLength: 3,
      maxLength: 200,
      pattern: /^[^<>]*$/,
      message: 'Title must be 3-200 characters without HTML'
    },
    description: {
      required: false,
      type: 'string',
      minLength: 10,
      maxLength: 5000,
      message: 'Description must be 10-5000 characters'
    },
    objective: {
      required: true,
      type: 'string',
      minLength: 10,
      maxLength: 500,
      message: 'Objective must be 10-500 characters'
    },
    keyResults: {
      required: true,
      type: 'array',
      minItems: 1,
      maxItems: 5,
      items: {
        type: 'string',
        minLength: 5,
        maxLength: 200
      },
      message: 'Must have 1-5 key results'
    },
    strategicAlignment: {
      required: false,
      type: 'string',
      maxLength: 200
    },
    owner: {
      required: true,
      type: 'string',
      minLength: 2,
      maxLength: 100,
      message: 'Owner name is required'
    },
    startDate: {
      required: false,
      type: 'date',
      min: () => new Date().toISOString().split('T')[0],
      message: 'Start date cannot be in the past'
    },
    endDate: {
      required: false,
      type: 'date',
      validator: (value, data) => {
        if (data.startDate && value) {
          return new Date(value) > new Date(data.startDate);
        }
        return true;
      },
      message: 'End date must be after start date'
    },
    status: {
      required: false,
      type: 'enum',
      values: ['not-started', 'in-progress', 'at-risk', 'completed', 'cancelled'],
      default: 'not-started'
    },
    progress: {
      required: false,
      type: 'number',
      min: 0,
      max: 100,
      default: 0
    },
    
    // System fields - commonly present in stored initiatives
    id: {
      required: false,
      // IDs are generated as strings like "init_..."; accept string
      type: 'string'
    },
    createdAt: {
      required: false,
      type: 'string'
    },
    updatedAt: {
      required: false,
      type: 'string'
    },
    timestamp: {
      required: false,
      type: 'string'
    },
    
    // Additional metadata fields
    priority: {
      required: false,
      type: 'string'
    },
    type: {
      required: false,
      type: 'string'
    },
    originalRowNumber: {
      required: false,
      type: 'number'
    },
    
    // AI enhancement fields
    aiImprovements: {
      required: false,
      type: 'array'
    },
    aiEnhanced: {
      required: false,
      type: 'boolean'
    },
    originalData: {
      required: false,
      type: 'object'
    },
    
    // Legacy/compatibility fields
    metrics: {
      required: false,
      type: 'array'
    },
    stakeholders: {
      required: false,
      type: 'array'
    },
    timeframe: {
      required: false,
      type: 'string'
    },
    // Common optional fields encountered in stored initiatives (suppress unknown warnings)
    createdBy: {
      required: false,
      type: 'string'
    },
    tags: {
      required: false,
      type: 'array'
    },
    risks: {
      required: false,
      type: 'array'
    },
    dependencies: {
      required: false,
      type: 'array'
    },
    attachments: {
      required: false,
      type: 'array'
    },

    // Analysis fields (added in v5.3.x)
    // strategicFit: 0-100 integer score generated by portfolio analysis
    strategicFit: {
      required: false,
      type: 'number',
      min: 0,
      max: 100
    },
    // analysisSummary: short model summary text
    analysisSummary: {
      required: false,
      type: 'string',
      maxLength: 10000
    },
    // analysisGaps: list of gap statements
    analysisGaps: {
      required: false,
      type: 'array',
      items: { type: 'string', maxLength: 1000 }
    },
    // analysisAt: ISO timestamp when analysis was produced
    analysisAt: {
      required: false,
      type: 'string'
    }
  },
  
  apiKey: {
    key: {
      required: true,
      type: 'string',
      // Updated pattern to support current OpenAI key formats:
      // - Can start with sk- or sk-proj-
      // - Followed by alphanumeric, hyphens, and underscores
      // - Variable length (typically 40-80 characters total)
      pattern: /^sk-[a-zA-Z0-9_-]{20,80}$/,
      minLength: 20,
      maxLength: 200,
      message: 'Invalid OpenAI API key format. Key should start with "sk-" followed by alphanumeric characters.'
    }
  },
  
  email: {
    email: {
      required: true,
      type: 'string',
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Invalid email address'
    }
  },
  
  fileUpload: {
    file: {
      required: true,
      type: 'file',
      maxSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: ['application/pdf', 'text/plain', 'application/json', 
                     'application/vnd.ms-excel', 
                     'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
      message: 'File must be PDF, TXT, JSON, XLS, or XLSX under 10MB'
    }
  },
  
  chatMessage: {
    message: {
      required: true,
      type: 'string',
      minLength: 1,
      maxLength: 4000,
      sanitize: true,
      message: 'Message must be 1-4000 characters'
    }
  }
};

class ValidationServiceClass {
  constructor() {
    this.schemas = { ...SCHEMAS };
    this.customValidators = new Map();
    this.sanitizers = new Map();
    this.errors = [];
    this._lastUnknownFields = []; // Track unknown fields for debugging
    this._lastUnknownFieldsKey = ''; // For de-duping console warnings
    
    // Register default sanitizers
    this.registerDefaultSanitizers();
  }
  
  /**
   * Register default sanitizers
   */
  registerDefaultSanitizers() {
    // HTML sanitizer
    this.sanitizers.set('html', (value) => {
      if (typeof value !== 'string') return value;
      return value
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;');
    });
    
    // Trim whitespace
    this.sanitizers.set('trim', (value) => {
      if (typeof value !== 'string') return value;
      return value.trim();
    });
    
    // Normalize spaces
    this.sanitizers.set('normalizeSpaces', (value) => {
      if (typeof value !== 'string') return value;
      return value.replace(/\s+/g, ' ').trim();
    });
    
    // Remove scripts
    this.sanitizers.set('removeScripts', (value) => {
      if (typeof value !== 'string') return value;
      return value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    });
  }
  
  /**
   * Validate data against a schema
   * @param {Object} data - Data to validate
   * @param {string|Object} schemaName - Schema name or custom schema
   * @returns {Object} Validation result
   */
  validate(data, schemaName) {
    this.errors = [];
    
    // Get schema
    const schema = typeof schemaName === 'string' 
      ? this.schemas[schemaName] 
      : schemaName;
    
    if (!schema) {
      throw new Error(`Schema '${schemaName}' not found`);
    }
    
    // Validate each field
    const validated = {};
    const errors = {};
    
    for (const [field, rules] of Object.entries(schema)) {
      const value = data[field];
      const result = this.validateField(value, rules, field, data);
      
      if (result.valid) {
        validated[field] = result.value;
      } else {
        errors[field] = result.error;
        this.errors.push({ field, error: result.error });
      }
    }
    
    // Check for extra fields (rate-limited to prevent console flooding)
    let unknownFields = [];
    for (const field of Object.keys(data)) {
      if (!schema[field]) {
        unknownFields.push(field);
      }
    }
    
    // Only warn about unknown fields in development and limit to prevent flooding
    if (unknownFields.length > 0) {
      // Create a stable key for de-duping identical warnings
      const key = unknownFields.slice().sort().join('|');
      if (import.meta.env.DEV && key !== this._lastUnknownFieldsKey) {
        // Group unknown fields to prevent console spam
        if (unknownFields.length <= 5) {
          console.warn(`Unknown fields in ${schemaName}:`, unknownFields.join(', '));
        } else {
          console.warn(`${unknownFields.length} unknown fields in ${schemaName}:`, unknownFields.slice(0, 5).join(', ') + '...');
        }
      }
      // Store for debugging and de-duping
      this._lastUnknownFields = unknownFields;
      this._lastUnknownFieldsKey = key;
    }
    
    const isValid = Object.keys(errors).length === 0;
    
    return {
      valid: isValid,
      data: validated,
      errors: isValid ? null : errors,
      errorCount: this.errors.length
    };
  }
  
  /**
   * Validate a single field
   */
  validateField(value, rules, fieldName, allData) {
    // Apply default if missing
    if (value === undefined || value === null || value === '') {
      if (rules.default !== undefined) {
        return { valid: true, value: rules.default };
      }
      if (rules.required) {
        return { valid: false, error: rules.message || `${fieldName} is required` };
      }
      return { valid: true, value: null };
    }
    
    // Sanitize if requested
    if (rules.sanitize) {
      value = this.sanitize(value, rules.sanitize);
    }
    
    // Type validation
    if (rules.type && !this.validateType(value, rules.type)) {
      return { 
        valid: false, 
        error: `${fieldName} must be a ${rules.type}` 
      };
    }
    
    // String validations
    if (rules.type === 'string') {
      if (rules.minLength && value.length < rules.minLength) {
        return { 
          valid: false, 
          error: `${fieldName} must be at least ${rules.minLength} characters` 
        };
      }
      if (rules.maxLength && value.length > rules.maxLength) {
        return { 
          valid: false, 
          error: `${fieldName} must be no more than ${rules.maxLength} characters` 
        };
      }
      if (rules.pattern && !rules.pattern.test(value)) {
        return { 
          valid: false, 
          error: rules.message || `${fieldName} format is invalid` 
        };
      }
    }
    
    // Number validations
    if (rules.type === 'number') {
      const num = Number(value);
      if (isNaN(num)) {
        return { valid: false, error: `${fieldName} must be a number` };
      }
      if (rules.min !== undefined && num < rules.min) {
        return { 
          valid: false, 
          error: `${fieldName} must be at least ${rules.min}` 
        };
      }
      if (rules.max !== undefined && num > rules.max) {
        return { 
          valid: false, 
          error: `${fieldName} must be no more than ${rules.max}` 
        };
      }
      value = num;
    }
    
    // Array validations
    if (rules.type === 'array') {
      if (!Array.isArray(value)) {
        return { valid: false, error: `${fieldName} must be an array` };
      }
      if (rules.minItems && value.length < rules.minItems) {
        return { 
          valid: false, 
          error: `${fieldName} must have at least ${rules.minItems} items` 
        };
      }
      if (rules.maxItems && value.length > rules.maxItems) {
        return { 
          valid: false, 
          error: `${fieldName} must have no more than ${rules.maxItems} items` 
        };
      }
      
      // Validate array items
      if (rules.items) {
        const validatedItems = [];
        for (let i = 0; i < value.length; i++) {
          const itemResult = this.validateField(value[i], rules.items, `${fieldName}[${i}]`, allData);
          if (!itemResult.valid) {
            return itemResult;
          }
          validatedItems.push(itemResult.value);
        }
        value = validatedItems;
      }
    }
    
    // Enum validation
    if (rules.type === 'enum' && rules.values) {
      if (!rules.values.includes(value)) {
        return { 
          valid: false, 
          error: `${fieldName} must be one of: ${rules.values.join(', ')}` 
        };
      }
    }
    
    // Date validation
    if (rules.type === 'date') {
      const date = new Date(value);
      if (isNaN(date.getTime())) {
        return { valid: false, error: `${fieldName} must be a valid date` };
      }
      
      if (rules.min) {
        const minDate = typeof rules.min === 'function' ? rules.min() : rules.min;
        if (date < new Date(minDate)) {
          return { 
            valid: false, 
            error: rules.message || `${fieldName} must be after ${minDate}` 
          };
        }
      }
      
      if (rules.max) {
        const maxDate = typeof rules.max === 'function' ? rules.max() : rules.max;
        if (date > new Date(maxDate)) {
          return { 
            valid: false, 
            error: rules.message || `${fieldName} must be before ${maxDate}` 
          };
        }
      }
    }
    
    // File validation
    if (rules.type === 'file') {
      if (!(value instanceof File)) {
        return { valid: false, error: `${fieldName} must be a file` };
      }
      
      if (rules.maxSize && value.size > rules.maxSize) {
        const sizeMB = (rules.maxSize / 1024 / 1024).toFixed(1);
        return { 
          valid: false, 
          error: `${fieldName} must be smaller than ${sizeMB}MB` 
        };
      }
      
      if (rules.allowedTypes && !rules.allowedTypes.includes(value.type)) {
        return { 
          valid: false, 
          error: `${fieldName} type not allowed. Allowed: ${rules.allowedTypes.join(', ')}` 
        };
      }
    }
    
    // Custom validator
    if (rules.validator) {
      const customResult = rules.validator(value, allData);
      if (customResult !== true) {
        return { 
          valid: false, 
          error: typeof customResult === 'string' ? customResult : rules.message 
        };
      }
    }
    
    return { valid: true, value };
  }
  
  /**
   * Validate type
   */
  validateType(value, type) {
    switch (type) {
      case 'string':
        return typeof value === 'string';
      case 'number':
        return typeof value === 'number' || !isNaN(Number(value));
      case 'boolean':
        return typeof value === 'boolean';
      case 'array':
        return Array.isArray(value);
      case 'object':
        return value !== null && typeof value === 'object' && !Array.isArray(value);
      case 'date':
        return !isNaN(new Date(value).getTime());
      case 'file':
        return value instanceof File;
      case 'enum':
        return true; // Enum validation happens separately
      default:
        return true;
    }
  }
  
  /**
   * Sanitize value
   */
  sanitize(value, sanitizers) {
    if (typeof sanitizers === 'boolean') {
      sanitizers = ['html', 'trim'];
    } else if (typeof sanitizers === 'string') {
      sanitizers = [sanitizers];
    }
    
    for (const sanitizer of sanitizers) {
      const fn = this.sanitizers.get(sanitizer);
      if (fn) {
        value = fn(value);
      }
    }
    
    return value;
  }
  
  /**
   * Add custom schema
   */
  addSchema(name, schema) {
    this.schemas[name] = schema;
  }
  
  /**
   * Add custom validator
   */
  addValidator(name, validator) {
    this.customValidators.set(name, validator);
  }
  
  /**
   * Add custom sanitizer
   */
  addSanitizer(name, sanitizer) {
    this.sanitizers.set(name, sanitizer);
  }
  
  /**
   * Validate initiative
   */
  validateInitiative(data) {
    return this.validate(data, 'initiative');
  }
  
  /**
   * Validate API key
   */
  validateApiKey(key) {
    return this.validate({ key }, 'apiKey');
  }
  
  /**
   * Validate email
   */
  validateEmail(email) {
    return this.validate({ email }, 'email');
  }
  
  /**
   * Validate file upload
   */
  validateFile(file) {
    return this.validate({ file }, 'fileUpload');
  }
  
  /**
   * Validate chat message
   */
  validateChatMessage(message) {
    return this.validate({ message }, 'chatMessage');
  }
  
  /**
   * Get validation errors as string
   */
  getErrorsAsString() {
    return this.errors.map(e => `${e.field}: ${e.error}`).join('\n');
  }
  
  /**
   * Clear errors
   */
  clearErrors() {
    this.errors = [];
  }
  
  /**
   * Check if form is valid (for UI)
   */
  isFormValid(formElement) {
    const formData = new FormData(formElement);
    const data = {};
    
    for (const [key, value] of formData.entries()) {
      data[key] = value;
    }
    
    // Determine schema from form attributes
    const schemaName = formElement.dataset.schema || 'initiative';
    const result = this.validate(data, schemaName);
    
    // Add error classes to invalid fields
    formElement.querySelectorAll('.error').forEach(el => {
      el.classList.remove('error');
      el.textContent = '';
    });
    
    if (!result.valid && result.errors) {
      for (const [field, error] of Object.entries(result.errors)) {
        const input = formElement.querySelector(`[name="${field}"]`);
        if (input) {
          input.classList.add('error');
          const errorEl = formElement.querySelector(`[data-error-for="${field}"]`);
          if (errorEl) {
            errorEl.textContent = error;
            errorEl.classList.add('error');
          }
        }
      }
    }
    
    return result;
  }
  
  /**
   * Get last unknown fields encountered (for debugging)
   */
  getLastUnknownFields() {
    return this._lastUnknownFields;
  }
  
  /**
   * Get validation status
   */
  getStatus() {
    return {
      schemas: Object.keys(this.schemas).length,
      validators: this.customValidators.size,
      sanitizers: this.sanitizers.size,
      currentErrors: this.errors.length,
      lastUnknownFields: this._lastUnknownFields.length
    };
  }
}

// Create singleton instance
const ValidationService = new ValidationServiceClass();

// Export service
export default ValidationService;

// Attach to window in development
if (import.meta.env.DEV) {
  window.ValidationService = ValidationService;
  console.log('🔧 ValidationService available at window.ValidationService');
  console.log('💡 Test: ValidationService.validateInitiative({ title: "Test", description: "Test description", objective: "Test objective", keyResults: ["KR1"], owner: "John" })');
}
